/**
 * W5 — reads `technician_workspace_day` while the workspace dialog is open (enabled only then).
 * Refetches on the shared minute tick, on the board's `ticket-updated` event and on realtime changes to
 * ticket_unattended_operations. Timer sessions are not in the realtime publication, so "as of" is always shown.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSharedMinuteTimer } from '@/hooks/useSharedTimer';
import type { TechnicianWorkspaceDayDto } from './buildTechnicianWorkspace';

export interface WorkspaceDayState {
  data: TechnicianWorkspaceDayDto | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useTechnicianWorkspaceDay(
  technicianId: string,
  dayLabel: string,
  enabled: boolean,
  shopId?: string | null,
): WorkspaceDayState {
  const [data, setData] = useState<TechnicianWorkspaceDayDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const tick = useSharedMinuteTimer();
  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data: d, error: e } = await supabase.rpc('technician_workspace_day' as never, {
        p_technician_id: technicianId,
        p_day: dayLabel,
      } as never);
      if (cancelled) return;
      setLoading(false);
      if (e) {
        setError(e.message);
        return;
      }
      setError(null);
      setData(d as unknown as TechnicianWorkspaceDayDto);
    })();
    return () => { cancelled = true; };
  }, [enabled, technicianId, dayLabel, nonce, tick]);

  useEffect(() => {
    if (!enabled) return;
    const onUpdated = () => refetch();
    window.addEventListener('ticket-updated', onUpdated);
    let channel: ReturnType<typeof supabase.channel> | null = null;
    if (shopId) {
      channel = supabase
        .channel(`w5-workspace-${technicianId}-${dayLabel}`)
        .on('postgres_changes' as never,
          { event: '*', schema: 'public', table: 'ticket_unattended_operations', filter: `shop_id=eq.${shopId}` } as never,
          () => refetch())
        .subscribe();
    }
    return () => {
      window.removeEventListener('ticket-updated', onUpdated);
      if (channel) void supabase.removeChannel(channel);
    };
  }, [enabled, shopId, technicianId, dayLabel, refetch]);

  return { data, loading, error, refetch };
}
